<template>
    <v-container fluid>
        <v-row>
            <v-col>
                <!--table hauptkonfigurationen -->
                <v-data-table-server
                    v-model:sort-by="sortBy"
                    :headers="headers"
                    :items="teilnehmerList"
                    :loading="loading"
                    :items-length="totalNumberOfRecords || 0"
                    :must-sort="true"
                    item-value="kundenNummer"
                    hover
                    hide-default-footer
                    class="elevation-1 nowhow-table"
                    @update:sort-by="onSortByChanged"
                >
                    <!-- Header der Table(Filter anzeige, gefundene Elemente) -->
                    <template #top>
                        <TableHeader :filters="filters" @chip-closed="removeFilter">
                            <template #default>
                                <div class="table-header-item-count">
                                    {{ totalNumberOfRecords + ' ' + $t('archiv.table.header.items') }}
                                </div>
                                <v-text-field
                                    v-if="showSearchBar"
                                    v-model="filterData.suchText"
                                    class="ml-5"
                                    prepend-inner-icon="mdi-magnify"
                                    append-inner-icon="mdi-close"
                                    :label="$t('suche')"
                                    hide-details
                                    autofocus
                                    @update:model-value="searchFieldInputChanged"
                                    @click:append-inner="searchBarClosed"
                                ></v-text-field>
                                <v-spacer />
                                <v-tooltip v-if="!showSearchBar" top color="secondary">
                                    <template #activator="{ props }">
                                        <v-btn v-bind="props" icon color="primary">
                                            <v-icon @click="showSearchBar = !showSearchBar">mdi-magnify</v-icon>
                                        </v-btn>
                                    </template>
                                    <div>
                                        <span>
                                            {{ $t('tableheader.tooltip.search') }}
                                        </span>
                                    </div>
                                </v-tooltip>
                                <v-tooltip top color="secondary">
                                    <template #activator="{ props }">
                                        <v-btn v-bind="props" icon color="primary" @click="showFilterDrawer = !showFilterDrawer">
                                            <v-icon>mdi-filter-outline</v-icon>
                                        </v-btn>
                                    </template>
                                    <div>
                                        <span>
                                            {{ $t('tableheader.tooltip.filter') }}
                                        </span>
                                    </div>
                                </v-tooltip>
                            </template>
                        </TableHeader>
                    </template>

                    <template #item.kundenNummer="{ item }">
                        <TextHighlights :text="item.kundenNummer" :search-text="filterData.suchText" />
                    </template>
                    <template #item.firma="{ item }">
                        <TextHighlights :text="item.firma" :search-text="filterData.suchText" />
                    </template>
                    <template #item.plz="{ item }">
                        <TextHighlights :text="item.plz + ' ' + item.ort" :search-text="filterData.suchText" />
                    </template>

                    <template #item.teilnehmerStatusCode="{ item }">
                        {{ $t(item.teilnehmerStatusCode) }}
                    </template>

                    <template #item.burgermenu="{ item }">
                        <v-btn icon :href="item.burgermenu" flat @click="initTakeSpecific(item.teilnehmerId)">
                            <v-icon color="primary" icon="mdi-account-cog" />
                        </v-btn>
                    </template>
                </v-data-table-server>
            </v-col>
        </v-row>
        <!-- FilterMenü -->
        <FilterDrawer :visible="showFilterDrawer" @cancel="cancelFilter()" @confirm="confirmFilter()" @reset="resetFilter()">
            <SingleFilter :title="$t('taketeilnehmer.table.header.status')">
                <template #default>
                    <v-row>
                        <v-col cols="12">
                            <v-select v-model="filterData.status" filled dense hide-details :items="status"></v-select>
                        </v-col>
                    </v-row>
                </template>
            </SingleFilter>
        </FilterDrawer>
    </v-container>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-facing-decorator';
import { namespace } from 'vuex-facing-decorator';
import debounce from 'lodash/debounce';
import { TakeTeilnehmerApiDto, TeilnehmerListApiDto } from '@/api-schema';

import { NAME as NAME_PRAXISGRUPPEADMINISTRATION } from '../store';
import { NAME as NAME_SECURITY } from '@/store/modules/security';
import { NAME as NAME_HEADERNOTIFICATION } from '@/store/modules/headerNotification';
import { NAME as NAME_GENERAL } from '@/store/modules/general';

const praxisgruppeAdministrationModul = namespace(NAME_PRAXISGRUPPEADMINISTRATION);
const securityModul = namespace(NAME_SECURITY);
const headerModul = namespace(NAME_HEADERNOTIFICATION);
const generalModul = namespace(NAME_GENERAL);

import { SUBTITLE, FOOTERTITLE } from '@/store/modules/general/mutations';
import { TAKESPECIFIC, RESET, GET_TEILNEHMER, GET_NEXT_TEILNEHMER } from '../store/actions';
import { GET_TEILNEHMER_DATA, GET_TEILNEHMER_LOADING, GET_TEILNEHMER_TOTAL_NUMBER_OF_RECORDS } from '../store/getters';
import { HASACCESS } from '@/store/modules/security/getters';
import { SETACCESSDENIED } from '@/store/modules/security/mutations';
import { NEW_ALERT } from '@/store/modules/headerNotification/actions';

import { TeilnehmerFilterDto } from '@/types/TeilnehmerFilterDto';
import { TakeTeilnehmerDto } from '@/types/UserDtos';

import TableHeader from '@/components/TableHeader.vue';
import SingleFilter from '@/components/SingleFilter.vue';
import FilterDrawer from '@/components/FilterDrawer.vue';
import TextHighlights from '@/components/TextHighlight/TextHighlights.vue';
import { APPLY_TEILNEHMERFILTER, CLEAR_TEILNEHMERFILTER } from '../store/mutations';
import { TableHeaderFilterItem } from '@/types/TableHeaderFilterItem';

@Component({
    components: {
        TableHeader,
        SingleFilter,
        FilterDrawer,
        TextHighlights
    }
})
export default class PraxisgruppeTeilnehmerAdministration extends Vue {
    @generalModul.Mutation(SUBTITLE) setSubTitle!: Function;
    @generalModul.Mutation(FOOTERTITLE) setFooterTitle!: Function;

    @praxisgruppeAdministrationModul.Action(TAKESPECIFIC) takeSpecific!: Function;
    @praxisgruppeAdministrationModul.Action(RESET) reset!: Function;
    @praxisgruppeAdministrationModul.Action(GET_TEILNEHMER) getTeilnehmer!: Function;
    @praxisgruppeAdministrationModul.Action(GET_NEXT_TEILNEHMER) getNextTeilnehmer!: Function;
    @praxisgruppeAdministrationModul.Getter(GET_TEILNEHMER_DATA)
    teilnehmerList!: Array<TeilnehmerListApiDto>;
    @praxisgruppeAdministrationModul.Getter(GET_TEILNEHMER_LOADING) loading!: boolean;
    @praxisgruppeAdministrationModul.Getter(GET_TEILNEHMER_TOTAL_NUMBER_OF_RECORDS)
    totalNumberOfRecords!: number;
    @praxisgruppeAdministrationModul.Mutation(CLEAR_TEILNEHMERFILTER) clearFilter!: Function;
    @praxisgruppeAdministrationModul.Mutation(APPLY_TEILNEHMERFILTER) applyFilters!: Function;

    @securityModul.Getter(HASACCESS) hasAccess!: Function;
    @securityModul.Mutation(SETACCESSDENIED) setAccessDenied!: Function;

    @headerModul.Action(NEW_ALERT) alertMessage!: Function;

    filters: Array<TableHeaderFilterItem> = [];
    showSearchBar = true;
    showFilterDrawer = false;

    sortBy: [{ key: 'kundenNummer'; order: 'desc' }];

    filterData: TeilnehmerFilterDto = new TeilnehmerFilterDto();
    filterDataCopy: TeilnehmerFilterDto = new TeilnehmerFilterDto();
    get status(): Array<any> {
        return [
            {
                title: this.$t('taketeilnehmer.status.aktiv'),
                value: 'taketeilnehmer.status.aktiv'
            },
            {
                title: this.$t('taketeilnehmer.status.gesperrt'),
                value: 'taketeilnehmer.status.gesperrt'
            },
            {
                title: this.$t('taketeilnehmer.status.geloescht'),
                value: 'taketeilnehmer.status.geloescht'
            }
        ];
    }
    created() {
        this.setSubTitle(this.$t('praxisgruppe.taketeilnehmer.subtitle'));
        this.setFooterTitle('S720');
        if (!this.hasAccess('Navigation.PraxisgruppeTeilnehmerAdministration')) {
            this.setAccessDenied(true);
            this.alertMessage({ text: this.$t('errormessage.noaccesspage') });
        } else {
            this.setAccessDenied(false);
        }

        this.filterData.status = 'taketeilnehmer.status.aktiv';
        this.applyFilters(this.filterData);
        this.getTeilnehmer();
        this.showFilterDrawerChanged(true);
    }

    /* Funktion deklarieren, aber erst in mount mit debounce initialisieren */
    searchFieldInputChanged!: () => void;
    mounted() {
        this.searchFieldInputChanged = debounce(() => {
            this.applyFilters(this.filterData);
            this.getTeilnehmer();
        }, 400);
    }

    get headers(): Array<any> {
        return [
            {
                title: this.$t('table.header.tvs.nr'),
                key: 'kundenNummer'
            },
            {
                title: this.$t('taketeilnehmer.table.firma'),
                key: 'firma'
            },
            {
                title: this.$t('taketeilnehmer.table.plz'),
                key: 'plz'
            },
            {
                title: this.$t('taketeilnehmer.table.header.status'),
                key: 'teilnehmerStatusCode'
            },
            {
                title: '',
                key: 'burgermenu',
                align: 'center',
                width: '10',
                sortable: false
            }
        ];
    }

    private searchBarClosed() {
        this.showSearchBar = false;
        this.filterData.suchText = '';
        this.applyFilters(this.filterData);
        this.getTeilnehmer();
    }

    private cancelFilter() {
        this.filterData = Object.assign({}, this.filterDataCopy);
        this.showFilterDrawer = false;
        this.applyFilters(this.filterData);
        this.getTeilnehmer();
    }

    private resetFilter() {
        this.filterData = new TeilnehmerFilterDto();
        this.filterData.status = 'taketeilnehmer.status.aktiv';
        this.applyFilters(this.filterData);
    }

    private confirmFilter() {
        this.showFilterDrawer = false;
        this.applyFilters(this.filterData);
        this.getTeilnehmer();
    }

    private removeFilter(value: string) {
        this.filters = this.filters.filter(function (obj) {
            return obj.value !== value;
        });

        if (value === 'taketeilnehmer.table.header.status') {
            this.filterData.status = '';
        }
        this.applyFilters(this.filterData);
        this.getTeilnehmer();
    }
    private initTakeSpecific(teilnehmerId: number) {
        //  Rolle soll für die Praxisgruppe im Backend ermittelt werden. Deswegen geben wir hier NULL
        const teilnehmer: TakeTeilnehmerApiDto = new TakeTeilnehmerDto(null, teilnehmerId);
        this.takeSpecific(teilnehmer);
    }

    private customSort(items: any[]) {
        return items;
    }

    onSortByChanged(newSortBy: any) {
        this.sortBy = newSortBy;
        if (this.sortBy.length > 0) {
            const sortField = this.sortBy[0];

            this.filterData.sortBy = sortField.key.charAt(0).toUpperCase() + sortField.key.slice(1);
            this.filterData.sortOrder = sortField.order === 'desc' ? 'Descending' : 'Ascending';
        } else {
            this.filterData.sortBy = null;
            this.filterData.sortOrder = null;
        }

        this.applyFilters(this.filterData);
        this.getTeilnehmer();
    }

    @Watch('showFilterDrawer')
    showFilterDrawerChanged(newVal) {
        if (newVal) {
            this.filterDataCopy = Object.assign({}, this.filterData);
        }
        this.filters = [];
        if (this.filterData.status !== '') {
            this.filters = this.filters.concat({
                key: 'taketeilnehmer.table.header.status',
                nameResourceKey: 'taketeilnehmer.table.header.status',
                value: this.$t(this.filterData.status).toString()
            });
        }
    }
}
</script>

<style scoped>
.v-application a {
    color: #0d47a1;
    cursor: pointer;
}
</style>
